import { Command } from 'commander';
import { createRequire } from 'node:module';
import { readFileSync, readdirSync, existsSync } from 'node:fs';
import { join, dirname } from 'node:path';
import { fileURLToPath } from 'node:url';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';

export interface RecipeSummary {
  name: string;
  title: string;
  needs: string;
  category: string;
}

/** Resolve the recipes dir from the project's installed @cloudgrid-io/runtime,
 *  falling back to the sibling package inside this monorepo (for dev + tests). */
export function resolveRecipesDir(fromDir: string = process.cwd()): string | null {
  try {
    const require = createRequire(join(fromDir, 'noop.js'));
    const pkgJson = require.resolve('@cloudgrid-io/runtime/package.json');
    const dir = join(dirname(pkgJson), 'recipes');
    if (existsSync(dir)) return dir;
  } catch {
    // not installed here — fall through
  }
  // In-repo fallback: packages/runtime/recipes, reached either from the bundled
  // dist (packages/cli/dist/index.js) or the source (packages/cli/src/commands).
  const here = dirname(fileURLToPath(import.meta.url));
  for (const rel of ['../../../runtime/recipes', '../../runtime/recipes']) {
    const repoDir = join(here, rel);
    if (existsSync(repoDir)) return repoDir;
  }
  return null;
}

function frontMatter(md: string): Record<string, string> {
  const m = md.match(/^---\n([\s\S]*?)\n---/);
  const out: Record<string, string> = {};
  if (!m) return out;
  for (const line of m[1].split('\n')) {
    const kv = line.match(/^([a-z_]+):\s*(.*)$/);
    if (kv) out[kv[1]] = kv[2];
  }
  return out;
}

export function listRecipes(dir: string): RecipeSummary[] {
  return readdirSync(dir)
    .filter((f) => f.endsWith('.md') && f !== 'INDEX.md')
    .map((f) => {
      const fm = frontMatter(readFileSync(join(dir, f), 'utf8'));
      return { name: fm.name ?? f.replace(/\.md$/, ''), title: fm.title ?? '', needs: fm.needs ?? '', category: fm.category ?? '' };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function readRecipe(dir: string, name: string): string {
  const file = join(dir, `${name}.md`);
  if (!existsSync(file)) {
    const available = listRecipes(dir).map((r) => r.name).join(', ');
    throw new Error(`unknown recipe "${name}". Available: ${available}`);
  }
  return readFileSync(file, 'utf8');
}

interface RecipesOpts {
  json?: boolean;
  output?: string;
}

export async function runRecipes(name: string | undefined, opts: RecipesOpts): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const dir = resolveRecipesDir();
  if (!dir) {
    out.human(() => 'Recipes ship with the Runtime SDK. Install it: npm i @cloudgrid-io/runtime');
    out.json(() => ({ error: 'RECIPES_NOT_FOUND', recipes: [] }));
    process.exitCode = 1;
    return;
  }
  if (!name) {
    const items = listRecipes(dir);
    out.json(() => ({ recipes: items }));
    out.human(() => readFileSync(join(dir, 'INDEX.md'), 'utf8'));
    return;
  }
  try {
    const md = readRecipe(dir, name);
    out.json(() => ({ name, markdown: md }));
    out.human(() => md);
  } catch (err) {
    out.human(() => (err as Error).message);
    out.json(() => ({ error: (err as Error).message, available: listRecipes(dir).map((r) => r.name) }));
    process.exitCode = 1;
  }
}

export const recipesCommand = addOutputOption(
  new Command('recipes')
    .description('Print Runtime SDK recipes (agent-first cookbook)')
    .argument('[name]', 'Recipe name (omit to print the index)')
    .option('--json', 'Machine-readable JSON output (alias of -o json)'),
).action(async (name: string | undefined, opts: RecipesOpts) => {
  await runRecipes(name, opts);
});
